
import Vue, {
  computed,
  defineComponent,
  onBeforeUnmount,
  onMounted,
  Ref,
  ref,
  watch
} from 'vue';
import { useAccessibleFocus } from '../composables/useAccessibleFocus';
import DocumentCover from './renderless/DocumentCover.vue';
import BaseButton from './BaseButton.vue';

enum MODAL_SIZES {
  NORMAL = 'normal',
  LARGE = 'large'
}

export default defineComponent({
  name: 'BaseModal',
  components: {
    DocumentCover,
    BaseButton
  },
  props: {
    title: {
      type: String,
      required: true
    },
    visible: {
      type: Boolean,
      required: false
    },
    showClose: {
      type: Boolean,
      default: true
    },
    size: {
      type: String,
      default: MODAL_SIZES.NORMAL,
      validator: (value: string) => {
        return [...(Object.values(MODAL_SIZES) as string[])].includes(value);
      }
    },
    value: {
      type: Boolean,
      default: false
    }
  },
  setup(props, context) {
    const modalContent: Ref<HTMLElement> = ref(null);
    const modalClose: Ref<Vue> = ref(null);
    const { setFocus, resetFocus } = useAccessibleFocus();

    const internalValue = computed({
      get(): boolean {
        return props.value;
      },

      set(value: boolean) {
        context.emit('input', value);
      }
    });

    const hasFooter = computed((): boolean => {
      return !!context.slots.footer;
    });

    const isVisible = computed((): boolean => {
      // Support the use of visible prop or v-model
      return props.visible || !!internalValue.value;
    });

    watch(internalValue, () => {
      toggleVisible();
    });
    watch(
      () => props.visible,
      () => {
        toggleVisible();
      }
    );

    async function handleShow() {
      // Disable body scrolling when modal visible
      document.body.style.overflow = 'hidden';
      await setFocus(modalContent, modalClose);
    }

    function handleHide() {
      resetFocus();
      document.body.style.overflow = '';
    }

    function toggleVisible() {
      if (isVisible.value) {
        handleShow();
      } else {
        handleHide();
      }
    }

    function hide() {
      internalValue.value = false;
      context.emit('close');
    }

    onMounted(() => {
      if (isVisible.value) {
        handleShow();
      }
    });

    onBeforeUnmount(() => {
      handleHide();
    });

    return {
      hasFooter,
      isVisible,
      toggleVisible,
      hide,
      modalClose,
      modalContent
    };
  }
});
